import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
  cloneGiftWithPurchaseConfig,
  createGiftEntityId,
  emptyQuotasForGroups,
  getSortedRewardGroups,
  getSortedTiers,
  validateGiftWithPurchaseDraft,
} from '../../../api/marketing/gifts/giftWithPurchaseConfigHelpers'
import {
  areGiftConfigsEqual,
  createDefaultGiftConfigSnapshot,
} from '../../../api/marketing/gifts/mapGiftWithPurchaseConfig'
import getGiftWithPurchaseConfig from '../../../api/marketing/gifts/getGiftWithPurchaseConfig'
import saveGiftWithPurchaseConfigApi from '../../../api/marketing/gifts/saveGiftWithPurchaseConfig'

const GiftsConfigContext = createContext(null)

const UNSUPPORTED_MESSAGE =
  'Конфігурацію оновлено в новішій версії BackOffice. Оновіть додаток перед редагуванням.'

export function GiftsConfigProvider({ children }) {
  const [draft, setDraft] = useState(null)
  const [lastSaved, setLastSaved] = useState(null)
  const [loadStatus, setLoadStatus] = useState('loading')
  const [loadError, setLoadError] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [loadNonce, setLoadNonce] = useState(0)

  const reloadConfig = useCallback(() => {
    setLoadNonce((value) => value + 1)
  }, [])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setLoadStatus('loading')
      setLoadError(null)
      setDraft(null)
      setLastSaved(null)

      try {
        const result = await getGiftWithPurchaseConfig()

        if (cancelled) {
          return
        }

        if (result.status === 'missing') {
          const defaults = createDefaultGiftConfigSnapshot()
          setDraft(defaults)
          setLastSaved(cloneGiftWithPurchaseConfig(defaults))
          setLoadStatus('missing')
          return
        }

        if (result.status === 'unsupported') {
          setDraft(result.config)
          setLastSaved(cloneGiftWithPurchaseConfig(result.config))
          setLoadStatus('unsupported')
          setLoadError(UNSUPPORTED_MESSAGE)
          return
        }

        setDraft(result.config)
        setLastSaved(cloneGiftWithPurchaseConfig(result.config))
        setLoadStatus('ready')
      } catch (error) {
        console.error('[GiftsConfigProvider] load failed:', error)

        if (cancelled) {
          return
        }

        setDraft(null)
        setLastSaved(null)
        setLoadStatus('error')
        setLoadError(
          error?.message || 'Не вдалося завантажити налаштування подарунків. Спробуйте ще раз.'
        )
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [loadNonce])

  const isEditable = loadStatus === 'missing' || loadStatus === 'ready'
  const isDirty = useMemo(() => {
    if (!isEditable || !draft || !lastSaved) {
      return false
    }

    return !areGiftConfigsEqual(draft, lastSaved)
  }, [draft, lastSaved, isEditable])

  const resetToBaseline = useCallback(() => {
    if (!lastSaved) {
      return
    }

    setDraft(cloneGiftWithPurchaseConfig(lastSaved))
  }, [lastSaved])

  const setApplyToOutlet = useCallback((applyToOutlet) => {
    setDraft((prev) => (prev ? { ...prev, applyToOutlet: Boolean(applyToOutlet) } : prev))
  }, [])

  const updateTierMinAmount = useCallback((tierId, minAmount) => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      return {
        ...prev,
        tiers: prev.tiers.map((tier) => (tier.id === tierId ? { ...tier, minAmount } : tier)),
      }
    })
  }, [])

  const updateTierQuota = useCallback((tierId, groupId, quantity) => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      return {
        ...prev,
        tiers: prev.tiers.map((tier) => {
          if (tier.id !== tierId) {
            return tier
          }

          return {
            ...tier,
            quotas: {
              ...tier.quotas,
              [groupId]: quantity,
            },
          }
        }),
      }
    })
  }, [])

  const addTier = useCallback(() => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      const sorted = getSortedTiers(prev.tiers)
      const lastAmount = sorted.length ? sorted[sorted.length - 1].minAmount : 0
      const nextAmount = lastAmount + 1000

      return {
        ...prev,
        tiers: [
          ...prev.tiers,
          {
            id: createGiftEntityId('tier'),
            minAmount: nextAmount,
            sortOrder: prev.tiers.length,
            quotas: emptyQuotasForGroups(prev.rewardGroups),
          },
        ],
      }
    })
  }, [])

  const removeTier = useCallback((tierId) => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      if (prev.packaging?.freeFromTierId === tierId) {
        return prev
      }

      if (prev.tiers.length <= 1) {
        return prev
      }

      return {
        ...prev,
        tiers: prev.tiers.filter((tier) => tier.id !== tierId),
      }
    })
  }, [])

  const updateGroupName = useCallback((groupId, name) => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      return {
        ...prev,
        rewardGroups: prev.rewardGroups.map((group) =>
          group.id === groupId ? { ...group, name } : group
        ),
      }
    })
  }, [])

  const addGroup = useCallback(() => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      const groupId = createGiftEntityId('group')
      const nextGroups = [
        ...prev.rewardGroups,
        {
          id: groupId,
          name: 'Нова група',
          sortOrder: prev.rewardGroups.length,
        },
      ]

      return {
        ...prev,
        rewardGroups: nextGroups,
        tiers: prev.tiers.map((tier) => ({
          ...tier,
          quotas: {
            ...tier.quotas,
            [groupId]: 0,
          },
        })),
      }
    })
  }, [])

  const removeGroup = useCallback((groupId) => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      if (prev.rewardGroups.length <= 1) {
        return prev
      }

      const nextGroups = prev.rewardGroups
        .filter((group) => group.id !== groupId)
        .map((group, index) => ({ ...group, sortOrder: index }))

      return {
        ...prev,
        rewardGroups: nextGroups,
        tiers: prev.tiers.map((tier) => {
          const nextQuotas = { ...tier.quotas }
          delete nextQuotas[groupId]
          return { ...tier, quotas: nextQuotas }
        }),
      }
    })
  }, [])

  const moveGroup = useCallback((groupId, direction) => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      const sorted = getSortedRewardGroups(prev.rewardGroups)
      const index = sorted.findIndex((group) => group.id === groupId)
      const targetIndex = direction === 'up' ? index - 1 : index + 1

      if (index < 0 || targetIndex < 0 || targetIndex >= sorted.length) {
        return prev
      }

      const reordered = [...sorted]
      const [item] = reordered.splice(index, 1)
      reordered.splice(targetIndex, 0, item)

      return {
        ...prev,
        rewardGroups: reordered.map((group, order) => ({ ...group, sortOrder: order })),
      }
    })
  }, [])

  const setPackagingCategory = useCallback((categorySelection) => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      return {
        ...prev,
        packaging: {
          ...prev.packaging,
          categorySelection,
        },
      }
    })
  }, [])

  const setFreeFromTierId = useCallback((freeFromTierId) => {
    setDraft((prev) => {
      if (!prev) {
        return prev
      }

      return {
        ...prev,
        packaging: {
          ...prev.packaging,
          freeFromTierId: freeFromTierId || null,
        },
      }
    })
  }, [])

  const saveConfig = useCallback(async () => {
    if (!draft || !isEditable) {
      return {
        ok: false,
        message: loadError || 'Збереження недоступне',
      }
    }

    const validation = validateGiftWithPurchaseDraft(draft)
    if (!validation.ok) {
      return {
        ok: false,
        message: validation.errors[0] || 'Перевірте заповнення форми',
        validation,
      }
    }

    setIsSaving(true)

    try {
      const result = await saveGiftWithPurchaseConfigApi(draft)

      if (!result.ok) {
        return result
      }

      const saved = result.config || draft
      setDraft(cloneGiftWithPurchaseConfig(saved))
      setLastSaved(cloneGiftWithPurchaseConfig(saved))
      setLoadStatus('ready')
      setLoadError(null)

      return {
        ok: true,
        config: saved,
      }
    } finally {
      setIsSaving(false)
    }
  }, [draft, isEditable, loadError])

  const validation = useMemo(
    () => (draft ? validateGiftWithPurchaseDraft(draft) : { ok: false, errors: [] }),
    [draft]
  )

  const value = useMemo(
    () => ({
      draft,
      sortedTiers: getSortedTiers(draft?.tiers || []),
      sortedGroups: getSortedRewardGroups(draft?.rewardGroups || []),
      validation,
      loadStatus,
      loadError,
      isEditable,
      isDirty,
      isSaving,
      isLoading: loadStatus === 'loading',
      hasDraft: Boolean(draft),
      reloadConfig,
      resetToBaseline,
      saveConfig,
      setApplyToOutlet,
      updateTierMinAmount,
      updateTierQuota,
      addTier,
      removeTier,
      updateGroupName,
      addGroup,
      removeGroup,
      moveGroup,
      setPackagingCategory,
      setFreeFromTierId,
    }),
    [
      draft,
      validation,
      loadStatus,
      loadError,
      isEditable,
      isDirty,
      isSaving,
      reloadConfig,
      resetToBaseline,
      saveConfig,
      setApplyToOutlet,
      updateTierMinAmount,
      updateTierQuota,
      addTier,
      removeTier,
      updateGroupName,
      addGroup,
      removeGroup,
      moveGroup,
      setPackagingCategory,
      setFreeFromTierId,
    ]
  )

  return <GiftsConfigContext.Provider value={value}>{children}</GiftsConfigContext.Provider>
}

export function useGiftsConfig() {
  const ctx = useContext(GiftsConfigContext)

  if (!ctx) {
    throw new Error('useGiftsConfig must be used within GiftsConfigProvider')
  }

  return ctx
}
